import React, { useCallback, useEffect, useState } from 'react';
import { Button, Card, Select, Space, Table, Tag, Typography, message } from 'antd';
import type { ColumnsType, TablePaginationConfig } from 'antd/es/table';
import { ReloadOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import { cashflowDataApi, type CashflowManualData } from '../../services/statistics/cashflow-data';
import { useShopMap } from '../../services/store/organization.store';

const { Text, Title } = Typography;

type DataTypeFilter = 'all' | 'daily' | 'monthly';

const formatCurrency = (value: number): string =>
  `¥${(value || 0).toLocaleString('zh-CN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const getAmountTags = (record: CashflowManualData): string[] => {
  const items: Array<[string, number]> = [
    ['资金注入', record.capitalInjection],
    ['违约金', record.penaltyIncome],
    ['买断收入', record.buyoutIncome],
    ['处置收入', record.disposalIncome],
    ['设备采购金', record.devicePurchaseFund],
    ['库存残值', record.inventoryResidualValue],
    ['采购总成本', record.purchaseCost],
    ['水电费', record.electricityFee],
    ['房租费', record.rentFee],
    ['物业费', record.propertyFee],
    ['人力成本', record.laborCost],
  ];
  return items
    .filter(([, amount]) => Math.abs(amount || 0) > 0)
    .map(([label, amount]) => `${label}: ${formatCurrency(amount)}`);
};

const CashflowEntryRecords: React.FC = () => {
  const { shopList, shopMap } = useShopMap();
  const [loading, setLoading] = useState(false);
  const [records, setRecords] = useState<CashflowManualData[]>([]);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const [total, setTotal] = useState(0);
  const [storeId, setStoreId] = useState<number | undefined>(undefined);
  const [dataType, setDataType] = useState<DataTypeFilter>('all');

  const fetchRecords = useCallback(async () => {
    setLoading(true);
    try {
      const filter: Record<string, unknown> = {};
      if (storeId !== undefined) {
        filter.store_id = storeId;
      }
      if (dataType !== 'all') {
        filter.data_type = dataType;
      }
      const response = await cashflowDataApi.listManualData({
        page,
        pageSize,
        filter,
        sort: [{ field: 'created_at', order: 'desc' }],
      });
      setRecords(response.data.items || []);
      setTotal(response.data.pagination?.total || 0);
    } catch (error) {
      console.error('获取录入记录失败:', error);
      message.error('获取录入记录失败');
    } finally {
      setLoading(false);
    }
  }, [page, pageSize, storeId, dataType]);

  useEffect(() => {
    fetchRecords();
  }, [fetchRecords]);

  useEffect(() => {
    setPage(1);
  }, [storeId, dataType]);

  const columns: ColumnsType<CashflowManualData> = [
    {
      title: 'ID',
      dataIndex: 'id',
      key: 'id',
      width: 80,
      align: 'center',
    },
    {
      title: '录入时间',
      dataIndex: 'createdAt',
      key: 'createdAt',
      width: 180,
      render: (value: string) => dayjs(value).format('YYYY-MM-DD HH:mm:ss'),
    },
    {
      title: '业务日期',
      dataIndex: 'recordDate',
      key: 'recordDate',
      width: 120,
      render: (value: string) => dayjs(value).format('YYYY-MM-DD'),
    },
    {
      title: '店铺',
      dataIndex: 'storeId',
      key: 'storeId',
      width: 180,
      render: (value?: number) => {
        if (value === undefined || value === null) {
          return <Tag color="default">全部店铺</Tag>;
        }
        const shop = shopMap.get?.(Number(value));
        return shop?.name || String(value);
      },
    },
    {
      title: '数据类型',
      dataIndex: 'dataType',
      key: 'dataType',
      width: 100,
      render: (value: string) => (
        <Tag color={value === 'monthly' ? 'purple' : 'blue'}>{value === 'monthly' ? '月数据' : '日数据'}</Tag>
      ),
    },
    {
      title: '金额明细',
      key: 'amounts',
      render: (_, record) => {
        const tags = getAmountTags(record);
        if (tags.length === 0) {
          return <Text type="secondary">--</Text>;
        }
        return (
          <Space wrap>
            {tags.map((item) => (
              <Tag key={`${record.id}-${item}`}>{item}</Tag>
            ))}
          </Space>
        );
      },
    },
    {
      title: '录入人',
      key: 'creator',
      width: 160,
      render: (_, record) => `${record.creatorName} (#${record.creatorId})`,
    },
    {
      title: '备注',
      dataIndex: 'remark',
      key: 'remark',
      width: 220,
      render: (value?: string) => value || '--',
    },
  ];

  const pagination: TablePaginationConfig = {
    current: page,
    pageSize,
    total,
    showSizeChanger: true,
    showTotal: (t) => `共 ${t} 条`,
    onChange: (nextPage, nextPageSize) => {
      setPage(nextPage);
      setPageSize(nextPageSize || pageSize);
    },
  };

  return (
    <div style={{ padding: 24 }}>
      <Title level={2}>现金流录入记录</Title>

      <Card>
        <Space wrap style={{ marginBottom: 16 }}>
          <Select
            style={{ width: 220 }}
            placeholder="按店铺筛选"
            allowClear
            value={storeId}
            onChange={setStoreId}
            options={shopList.map((shop) => ({
              label: shop.name,
              value: Number(shop.code),
            }))}
          />
          <Select<DataTypeFilter>
            style={{ width: 160 }}
            value={dataType}
            onChange={setDataType}
            options={[
              { label: '全部类型', value: 'all' },
              { label: '日数据', value: 'daily' },
              { label: '月数据', value: 'monthly' },
            ]}
          />
          <Button icon={<ReloadOutlined />} onClick={fetchRecords}>
            刷新
          </Button>
        </Space>

        <Table<CashflowManualData>
          rowKey="id"
          loading={loading}
          columns={columns}
          dataSource={records}
          pagination={pagination}
          scroll={{ x: 1200 }}
          bordered
        />
      </Card>
    </div>
  );
};

export default CashflowEntryRecords;
